import React from "react";
import {
  Box,
  Typography,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import MapPanel from "@modules/stepper/components/MapPanel";
import { useGeolocation } from "@gis/components/useGeoLocation";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootReducerType } from "@store/store";
import * as Yup from "yup";
import {
  setLegacyCoordinateSite,
  setLegacyCoordinates,
  setLegacyField,
  setLegacyFields,
  setPropertyTypeId,
} from "../redux/slice/legacySlice";
import {
  fetchAccessLegacyDetailsThunk,
  fetchLegacySubStagesThunk,
  fetchFinalAccessLegacyDetailsThunk,
  getAuthorityListThunk,
  getPAListThunk,
  getROListThunk,
  updateFinalLegacyAccessApplicationThunk,
  updateLegacyAccessApplicationThunk,
} from "../redux/thunk/legacyThunk";
import { StyledTextField } from "@components/CustomInput/customInput";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getDeleteSignedURLThunk,
  getFileSignedURLThunk,
} from "@modules/stepper/redux/thunk/documentThunk";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { getTypeOfPropertyThunk } from "@modules/stepper/redux/thunk/sideRoadwaysThunk";
import { deleteFileOnAwsAPI } from "../../../utils/s3Upload";
import { getCategoryListThunk } from "@modules/stepper/redux/thunk/amenitiesThunk";
import { decrypt } from "../../../../src/utils/encryptionUtils";
import { SelectAmenityCategoryRetail } from "@modules/stepper/components/data";
import MapSearchInput from "@gis/components/MapSearchInput";
import { start } from "repl";
import AlertDialogBox from "@modules/stepper/components/AlertDialogBox";

// Utility function to convert date to ISO 8601 format (YYYY-MM-DDTHH:MM:SS.SSSZ)
const convertDateToISO8601 = (dateValue: any): string | null => {
  if (!dateValue) return null;
  try {
    const date = new Date(dateValue);
    return date.toISOString();
  } catch {
    return null;
  }
};

const LegacyAmenityDetails = ({
  initialValues,
  onNext,
  onBack,
  setSubmitHandler,
  setIsStepValid,
}: any) => {
  type LegacySubStage = {
    id: string;
    name: string;
    group: string;
  };

  const {
    start_latitude,
    start_longitude,
    end_latitude,
    end_longitude,
    application_category_id,
    access_category_id,
    access_category_name,
    // location_type_id,
    // location_type_name,
    coordinateSite,
    license_type: storedLicenseType,
    license_type_selection: storedLicenseTypeSelection,
    legacy_substage_id,
    legacy_substage_name,
  } = useSelector((state: RootReducerType) => state.accessLegacy);

  const { applicationDetails } = useSelector(
    (state: RootReducerType) => state.applicationDetails,
  );

  const { profile } = useSelector((state: RootReducerType) => state.profile);

  const organization = profile?.organizations?.[0];

  const hasOrganization = Boolean(
    organization?.gst_number &&
    organization?.business_name &&
    organization?.registration_date,
  );

  const { status_id } = applicationDetails || {};

  const [searchParams] = useSearchParams();
  const access = searchParams.get("access");

  const [amenityCategoryOptions, setAmenityCategoryOptions] = React.useState<
    any[]
  >([]);
  const [amenityOptions, setAmenityOptions] = React.useState<any[]>([]);
  const [uploadedFile, setUploadedFile] = React.useState<any>(null);
  const [legacyData, setLegacyData] = React.useState<any>(null);
  const [authorityOptions, setAuthorityOptions] = React.useState<any[]>([]);
  const [roOptions, setRoOptions] = React.useState<any[]>([]);
  const [piuOptions, setPiuOptions] = React.useState<any[]>([]);
  const [showStageTooltip, setShowStageTooltip] = React.useState(true);
  const [openGstDialog, setOpenGstDialog] = React.useState(false);
  const [subStageOptions, setSubStageOptions] = React.useState<
    LegacySubStage[]
  >([]);
  const [licenseTypeSelection, setLicenseTypeSelection] = React.useState<
    | "PROCESSING"
    | "PROVISIONAL_APPROVED"
    | "PROVISIONAL_UNDER_REVIEW"
    | "FINAL"
    | "FINAL_UNDER_REVIEW"
  >("PROCESSING");

  const isProvisionalUnderReviewSelection =
    licenseTypeSelection === "PROVISIONAL_UNDER_REVIEW";
  const isFinalUnderReviewSelection =
    licenseTypeSelection === "FINAL_UNDER_REVIEW";
  const shouldShowProvisionalCopy =
    licenseTypeSelection === "PROVISIONAL_APPROVED" ||
    licenseTypeSelection === "PROVISIONAL_UNDER_REVIEW" ||
    isFinalUnderReviewSelection;
  const shouldShowLicenseDocumentForType = (licenseType: string) => {
    // Show PROVISIONAL upload when not under review
    if (licenseType === "PROVISIONAL" && !isProvisionalUnderReviewSelection) {
      return true;
    }
    // Show PROVISIONAL upload when FINAL is under review
    if (licenseType === "FINAL" && isFinalUnderReviewSelection) {
      return true;
    }
    // Show FINAL upload when already approved
    if (licenseType === "FINAL" && !isFinalUnderReviewSelection) {
      return true;
    }
    return false;
  };

  const licenseTypeOptions = [
    { label: "Processing", value: "PROCESSING" },
    { label: "Provisional", value: "PROVISIONAL" },
    { label: "Final", value: "FINAL" },
  ];

  const inputSX = {
    "& .MuiOutlinedInput-root": {
      height: 36,
      fontSize: "12px",
      background: "#fff",
      // border: "1px solid #757575",
      outline: "none",
      borderRadius: "4px",
      "& fieldset": { borderColor: "#cbd5e1" },
      "&:hover fieldset": { borderColor: "#94a3b8" },
      "&.Mui-focused fieldset": { borderColor: "#3a1c73" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 10px",
    },
  };

  const labelWidth = 240;
  const fieldWidth = 400;

  const getMatchedSubStage = (
    legacySubStageId: string,
    options: LegacySubStage[],
  ) => {
    if (!legacySubStageId || !options.length) return null;

    const decryptedLegacyId = decrypt(legacySubStageId);

    return (
      options.find((item) => decrypt(item.id) === decryptedLegacyId) || null
    );
  };

  const fetchAmenityOptions = async (categoryId: any) => {
    try {
      const res = await dispatch(
        getTypeOfPropertyThunk({ category_id: categoryId }),
      );

      if (res.payload?.success) {
        const options = res.payload.data.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));

        setAmenityOptions(options);

        // ✅ auto select if legacy exists
        if (legacyData?.property_type_id) {
          formik.setFieldValue("amenity", legacyData.property_type_id);
        }
      }
    } catch (err) {
      console.error("Error fetching amenity options", err);
    }
  };

  const fetchAuthorities = async () => {
    try {
      const res = await dispatch(getAuthorityListThunk()).unwrap();

      if (res?.success) {
        setAuthorityOptions(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching authority list:", error);

      toast.error("Failed to fetch authorities");
    }
  };

  const fetchROList = async (authorityId: any) => {
    try {
      const res = await dispatch(getROListThunk(authorityId)).unwrap();

      if (res?.success) {
        setRoOptions(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching RO list:", error);

      setRoOptions([]);

      toast.error("Failed to fetch RO list");
    }
  };

  const fetchPIUList = async (officeId: any) => {
    try {
      const res = await dispatch(getPAListThunk(officeId)).unwrap();

      if (res?.success) {
        setPiuOptions(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching PIU list:", error);

      setPiuOptions([]);

      toast.error("Failed to fetch PIU list");
    }
  };

  React.useEffect(() => {
    fetchAuthorities();
  }, []);

  const mapLegacyResponse = (data: any) => {
    const normalizeHighwayCategory = (value: any) => {
      if (!value) return "";
      const normalized = String(value)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

      const map: Record<string, string> = {
        "2 lane": "2-Lane",
        "2-lane": "2-Lane",
        "4 lane": "4-Lane",
        "4-lane": "4-Lane",
        "6 lane": "6-Lane",
        "6-lane": "6-Lane",
        "8 lane": "8-Lane",
        "8-lane": "8-Lane",
        "il/sl": "IL / SL",
        "il / sl": "IL / SL",
      };

      return map[normalized] || String(value).trim();
    };

    const normalizeHighwaySide = (value: any) => {
      if (!value) return "";
      const normalized = String(value)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

      const map: Record<string, string> = {
        "left hand side": "Left-Hand Side",
        "left-hand side": "Left-Hand Side",
        "right hand side": "Right-Hand Side",
        "right-hand side": "Right-Hand Side",
        "both side": "Both Side of the Highway",
        "both sides": "Both Side of the Highway",
        "both side of highway": "Both Side of the Highway",
        "both side of the highway": "Both Side of the Highway",
      };

      return map[normalized] || String(value).trim();
    };

    const [startKm, startM] = data.start_chainage?.split("+") || [];
    const [endKm, endM] = data.end_chainage?.split("+") || [];

    const formatDateOnly = (value: any) =>
      typeof value === "string" && value.includes("T")
        ? value.split("T")[0]
        : value || "";

    const resolvedLicenseType =
      data.application_phase ||
      (data.final_licence_url || data.final_license_url
        ? "FINAL"
        : data.provisional_licence_url || data.provisional_license_url
          ? "PROVISIONAL"
          : "PROCESSING");

    const issuedDateRaw =
      resolvedLicenseType === "FINAL"
        ? data.final_issued_date || data.final_noc_issued_date
        : resolvedLicenseType === "PROVISIONAL"
          ? data.provisional_issued_date
          : data.provisional_issued_date ||
          data.final_issued_date ||
          data.final_noc_issued_date;

    const expiryDateRaw =
      resolvedLicenseType === "FINAL"
        ? data.final_expiry_date || data.final_noc_expiry_date
        : resolvedLicenseType === "PROVISIONAL"
          ? data.provisional_expiry_date
          : data.provisional_expiry_date ||
          data.final_expiry_date ||
          data.final_noc_expiry_date;

    // ✅ file prefill
    if (data.file_name) {
      setUploadedFile({
        name: data.file_name,
        fileName: data.document_name,
        objectKey: data.upload_path,
      });
    }

    return {
      ...data,

      // ✅ chainage split
      start_chainage_km: startKm || "",
      start_chainage_m: startM || "",
      end_chainage_km: endKm || "",
      end_chainage_m: endM || "",

      // ✅ clean khasra
      khasra_number: data.khasra_number?.trim() || "",

      highway_category: normalizeHighwayCategory(data.highway_category),
      highway_side: normalizeHighwaySide(data.highway_side),

      // ✅ date format
      license_type: resolvedLicenseType,
      licenseIssued: formatDateOnly(issuedDateRaw),
      licenseExpiry: formatDateOnly(expiryDateRaw),
    };
  };

  const fetchLegacyDetails = async (access: string) => {
    try {
      const res = await dispatch(
        fetchAccessLegacyDetailsThunk(access as any),
      ).unwrap();

      const mappedData = mapLegacyResponse(res.data);

      const resolvedType =
        res.data?.application_phase || mappedData?.license_type || "PROCESSING";
      dispatch(setLegacyField({ name: "license_type", value: resolvedType }));

      setLegacyData(mappedData);

      // =========================================
      // AUTHORITIES
      // =========================================

      const authorityRes = await dispatch(getAuthorityListThunk()).unwrap();

      const authorityList = authorityRes?.data || [];

      setAuthorityOptions(authorityList);

      // decrypt dropdown encrypted_id
      const authorityId = await decrypt(mappedData.authority_id);

      const matchedAuthority = (
        await Promise.all(
          authorityList.map(async (item: any) => ({
            item,
            decryptedId: await decrypt(item.id),
          })),
        )
      ).find((x) => x.decryptedId === authorityId)?.item;

      // =========================================
      // RO LIST
      // =========================================

      let roList: any[] = [];

      if (matchedAuthority?.id) {
        const roRes = await dispatch(
          getROListThunk(matchedAuthority.id),
        ).unwrap();

        roList = roRes?.data || [];

        setRoOptions(roList);
      }

      const roId = await decrypt(mappedData.ro_id);

      const matchedRO = (
        await Promise.all(
          roList.map(async (item: any) => ({
            item,
            decryptedId: await decrypt(item.office_id),
          })),
        )
      ).find((x) => x.decryptedId === roId)?.item;

      // =========================================
      // PIU LIST
      // =========================================

      let piuList: any[] = [];

      if (matchedRO?.office_id) {
        const piuRes = await dispatch(
          getPAListThunk(matchedRO.office_id),
        ).unwrap();

        piuList = piuRes?.data || [];

        setPiuOptions(piuList);
      }

      const piuId = await decrypt(mappedData.piu_id);

      const matchedPIU = (
        await Promise.all(
          piuList.map(async (item: any) => ({
            item,
            decryptedId: await decrypt(item.office_id),
          })),
        )
      ).find((x) => x.decryptedId === piuId)?.item;

      formik.setValues({
        taluka: mappedData?.taluka || "",
        khasra_number: mappedData?.khasra_number || "",
        amenityType: mappedData?.access_category_name || "",
        amenity: mappedData?.property_type_id || "",

        authority_id: matchedAuthority?.id || "",

        ro_id: matchedRO?.office_id || "",

        piu_id: matchedPIU?.office_id || "",

        licenseExpiry: mappedData?.licenseExpiry || "",
        licenseIssued: mappedData?.licenseIssued || "",

        startKm: mappedData?.start_chainage_km || "",
        startM: mappedData?.start_chainage_m || "",
        endKm: mappedData?.end_chainage_km || "",
        endM: mappedData?.end_chainage_m || "",

        start_latitude: mappedData?.start_latitude || 0,
        start_longitude: mappedData?.start_longitude || 0,
        end_latitude: mappedData?.end_latitude || 0,
        end_longitude: mappedData?.end_longitude || 0,
        license_type:
          mappedData?.license_type ||
          mappedData?.application_phase ||
          "PROCESSING",
        legacy_substage_id: mappedData?.legacy_substage_id
          ? String(mappedData.legacy_substage_id)
          : "",

        address_1: mappedData?.address_1 || "",
        address_2: mappedData?.address_2 || "",
        state: mappedData?.state || "",
        district: mappedData?.district || "",
        pincode: mappedData?.pincode || "",
        highway_category: mappedData?.highway_category || "",
        national_highway_number: mappedData?.national_highway_number || "",
        village: mappedData?.village || "",
        highway_side: mappedData?.highway_side || "",
        // location_type_id: mappedData?.location_type_id || "",
        // location_type_name: mappedData?.location_type_name || "",
      });

      const mappedLicenseType =
        mappedData?.license_type ||
        mappedData?.application_phase ||
        "PROCESSING";
      let mappedLicenseTypeSelection:
        | "PROCESSING"
        | "PROVISIONAL_APPROVED"
        | "PROVISIONAL_UNDER_REVIEW"
        | "FINAL"
        | "FINAL_UNDER_REVIEW" = "PROCESSING";

      if (mappedLicenseType === "PROVISIONAL") {
        const hasSubStage = Boolean(mappedData?.legacy_substage_id);
        mappedLicenseTypeSelection = hasSubStage
          ? "PROVISIONAL_UNDER_REVIEW"
          : "PROVISIONAL_APPROVED";
        // setLicenseTypeSelection(mappedLicenseTypeSelection);
      } else if (mappedLicenseType === "FINAL") {
        mappedLicenseTypeSelection = "FINAL";
        // setLicenseTypeSelection("FINAL");
      } else {
        mappedLicenseTypeSelection = "PROCESSING";
        // setLicenseTypeSelection("PROCESSING");
      }

      dispatch(
        setLegacyFields({
          ...mappedData,
          access_category_id:
            mappedData?.access_category_id ??
            mappedData?.application_category_id ??
            "",
          license_type:
            mappedData?.license_type ||
            mappedData?.application_phase ||
            "PROCESSING",
          // license_type_selection: mappedLicenseTypeSelection,
          legacy_substage_id: mappedData?.legacy_substage_id
            ? String(mappedData.legacy_substage_id)
            : "",
          legacy_substage_name: mappedData?.legacy_substage_name || "",
        }),
      );

      return mappedData;
    } catch (error) {
      console.error("Error fetching legacy details:", error);
    }
  };

  const handleLicenseTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selection = e.target.value;

    const licenseType =
      selection === "PROVISIONAL_APPROVED" ||
        selection === "PROVISIONAL_UNDER_REVIEW"
        ? "PROVISIONAL"
        : selection === "FINAL_UNDER_REVIEW"
          ? "FINAL"
          : selection;

    setLicenseTypeSelection(selection as any);

    formik.setFieldValue("license_type", licenseType);

    dispatch(
      setLegacyFields({
        license_type: licenseType,
        license_type_selection: selection,
      }),
    );

    if (
      selection !== "PROVISIONAL_UNDER_REVIEW" &&
      selection !== "FINAL_UNDER_REVIEW"
    ) {
      formik.setFieldValue("legacy_substage_id", "");

      dispatch(
        setLegacyFields({
          legacy_substage_id: "",
          legacy_substage_name: "",
        }),
      );
    }

    setUploadedFile(null);
  };

  const updateLegacyApplication = async () => {
    try {
      const licenseType = formik.values.license_type;

      const basePayload: any = {
        application_id: access,

        // location_type_id: formik.values.location_type_id,

        start_latitude: start_latitude,
        start_longitude: start_longitude,

        start_chainage: `${formik.values.startKm}+${formik.values.startM}`,

        end_chainage: `${formik.values.endKm}+${formik.values.endM}`,

        access_category_id,

        taluka: formik.values.taluka,

        khasra_number: formik.values.khasra_number,

        property_type_id: formik.values.amenity,

        module_type: "access",

        authority_id: formik.values.authority_id,

        ro_id: formik.values.ro_id,

        piu_id: formik.values.piu_id,

        // licence_expiry_date: formik.values.licenseExpiry
        //   ? convertDateToISO8601(formik.values.licenseExpiry)
        //   : null,

        application_phase: licenseType,
        legacy_substage_id: formik.values.legacy_substage_id || null,

        // Address and location fields
        address_1: formik.values.address_1,
        address_2: formik.values.address_2,
        state: formik.values.state,
        pincode: formik.values.pincode,
        district: formik.values.district,
        highway_category: formik.values.highway_category,
        national_highway_number: formik.values.national_highway_number,
        village: formik.values.village,
        highway_side: formik.values.highway_side,
        // location_type_name: formik.values.location_type_name,
      };

      // =========================================================
      // PROCESSING
      // =========================================================
      if (licenseType === "PROCESSING") {
        const payload = {
          ...basePayload,

          // clear provisional
          provisional_licence_url: null,
          provisional_expiry_date: null,
          provisional_issued_date: null,

          // clear final
          final_licence_url: null,
          final_expiry_date: null,
          final_issued_date: null,

          file_name: null,
          document_name: null,
          document_type: null,
          upload_path: null,
        };

        const res = await dispatch(
          updateLegacyAccessApplicationThunk(payload),
        ).unwrap();

        toast.success(res.message || "Legacy application updated successfully");

        return;
      }

      // =========================================================
      // PROVISIONAL
      // =========================================================
      if (licenseType === "PROVISIONAL") {
        const payload = {
          ...basePayload,

          // provisional only
          provisional_licence_url: uploadedFile?.objectKey || null,

          provisional_expiry_date: formik.values.licenseExpiry
            ? convertDateToISO8601(formik.values.licenseExpiry)
            : null,

          provisional_issued_date: formik.values.licenseIssued
            ? convertDateToISO8601(formik.values.licenseIssued)
            : null,

          // clear final
          final_licence_url: null,
          final_expiry_date: null,
          final_issued_date: null,

          file_name: uploadedFile?.name || null,

          document_name: uploadedFile?.fileName || null,

          document_type: "PROVISIONAL_FILE",

          upload_path: uploadedFile?.objectKey || null,
        };

        const res = await dispatch(
          updateLegacyAccessApplicationThunk(payload),
        ).unwrap();

        toast.success(res.message || "Legacy application updated successfully");

        return;
      }

      // =========================================================
      // FINAL
      // =========================================================
      if (licenseType === "FINAL") {
        const isFinalUnderReviewFlow = isFinalUnderReviewSelection;

        const payload = {
          ...basePayload,

          // For FINAL under review, provisional doc/date fields are captured.
          provisional_licence_url: isFinalUnderReviewFlow
            ? uploadedFile?.objectKey || null
            : null,

          provisional_expiry_date: isFinalUnderReviewFlow
            ? formik.values.licenseExpiry
              ? convertDateToISO8601(formik.values.licenseExpiry)
              : null
            : null,

          provisional_issued_date: isFinalUnderReviewFlow
            ? formik.values.licenseIssued
              ? convertDateToISO8601(formik.values.licenseIssued)
              : null
            : null,

          final_licence_url: isFinalUnderReviewFlow
            ? null
            : uploadedFile?.objectKey || null,

          final_expiry_date: isFinalUnderReviewFlow
            ? null
            : formik.values.licenseExpiry
              ? convertDateToISO8601(formik.values.licenseExpiry)
              : null,

          final_issued_date: isFinalUnderReviewFlow
            ? null
            : formik.values.licenseIssued
              ? convertDateToISO8601(formik.values.licenseIssued)
              : null,

          file_name: uploadedFile?.name || null,

          document_name: uploadedFile?.fileName || null,

          document_type: isFinalUnderReviewFlow
            ? "PROVISIONAL_FILE"
            : "FINAL_FILE",

          upload_path: uploadedFile?.objectKey || null,
        };

        const res = await dispatch(
          updateFinalLegacyAccessApplicationThunk(payload),
        ).unwrap();

        toast.success(
          res.message || "Final legacy application updated successfully",
        );

        return;
      }
    } catch (error) {
      console.error(error);

      toast.error("Something went wrong!");

      throw error;
    }
  };

  const validationSchema = Yup.object({
    taluka: Yup.string()
      .matches(/^[a-zA-Z0-9\s.'-]*$/, "Only letters, numbers, spaces, dots, and hyphens are allowed")
      .required("Taluka is required"),

    khasra_number: Yup.string().required("Khasra number is required"),

    amenity: Yup.string().required("Amenity is required"),

    licenseExpiry: Yup.string().when("license_type", {
      is: (value: string) =>
        value &&
        (value == "PROCESSING" ||
          isProvisionalUnderReviewSelection ||
          isFinalUnderReviewSelection),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("License expiry date is required"),
    }),

    licenseIssued: Yup.string().when("license_type", {
      is: (value: string) =>
        value &&
        (value == "PROCESSING" ||
          isProvisionalUnderReviewSelection ||
          isFinalUnderReviewSelection),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("License issued date is required"),
    }),

    startKm: Yup.string()
      .matches(/^\d*\.?\d*$/, "Only numbers and decimal are allowed")
      .required("Start Chainage km is required"),

    startM: Yup.string()
      .matches(/^\d*\.?\d*$/, "Only numbers and decimal are allowed")
      .required("Start Chainage m is required"),

    endKm: Yup.string()
      .matches(/^\d*\.?\d*$/, "Only numbers and decimal are allowed")
      .required("End Chainage km is required"),

    endM: Yup.string()
      .matches(/^\d*\.?\d*$/, "Only numbers and decimal are allowed")
      .required("End Chainage m is required"),
    legacy_substage_id: Yup.string().when("license_type", {
      is: (value: string) => {
        if (!value) return false;
        return (
          licenseTypeSelection === "PROVISIONAL_UNDER_REVIEW" ||
          licenseTypeSelection === "FINAL_UNDER_REVIEW"
        );
      },
      then: (schema) => schema.required("Please select sub-stage"),
      otherwise: (schema) => schema.notRequired(),
    }),
    authority_id: Yup.string().required("Authority is required"),
    ro_id: Yup.string().required("RO is required"),
    piu_id: Yup.string().required("PIU is required"),
    address_1: Yup.string().required("Address line 1 is required"),
    address_2: Yup.string(),
    state: Yup.string()
      .matches(/^[a-zA-Z\s.'-]*$/, "Only letters, spaces, dots, and hyphens are allowed")
      .required("State is required"),

    district: Yup.string()
      .matches(/^[a-zA-Z\s.'-]*$/, "Only letters, spaces, dots, and hyphens are allowed")
      .required("District is required"),
    pincode: Yup.string()
      .matches(/^[0-9]{6}$/, "Pincode must be exactly 6 digits")
      .required("Pincode is required"),
    highway_category: Yup.string().required("Highway Category is required"),
    national_highway_number: Yup.string().required(
      "National Highway Number is required",
    ),
    village: Yup.string().required("Village is required"),
    highway_side: Yup.string().required("Side of Highway is Required"),
    // location_type_id: Yup.string().required("Stretch Type is required"),
    // location_type_name: Yup.string(),
  });

  const formik = useFormik({
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      taluka: legacyData?.taluka || "",
      khasra_number: legacyData?.khasra_number || "",
      amenityType: legacyData?.access_category_name || "",
      amenity: legacyData?.property_type_id || "",
      licenseExpiry: legacyData?.licenseExpiry || "",
      licenseIssued: legacyData?.licenseIssued || "",

      startKm: legacyData?.start_chainage_km || "",
      startM: legacyData?.start_chainage_m || "",
      endKm: legacyData?.end_chainage_km || "",
      endM: legacyData?.end_chainage_m || "",

      start_latitude: legacyData?.start_latitude || 0,
      start_longitude: legacyData?.start_longitude || 0,
      end_latitude: legacyData?.end_latitude || 0,
      end_longitude: legacyData?.end_longitude || 0,
      authority_id: legacyData?.authority_id || "",
      ro_id: legacyData?.ro_id || "",
      piu_id: legacyData?.piu_id || "",
      license_type:
        legacyData?.license_type ||
        legacyData?.application_phase ||
        storedLicenseType ||
        "PROCESSING",
      legacy_substage_id: legacyData?.legacy_substage_id
        ? String(legacyData.legacy_substage_id)
        : "",
      address_1: legacyData?.address_1 || "",
      address_2: legacyData?.address_2 || "",
      state: legacyData?.state || "",
      district: legacyData?.district || "",
      pincode: legacyData?.pincode || "",
      highway_category: legacyData?.highway_category || "",
      national_highway_number: legacyData?.national_highway_number || "",
      village: legacyData?.village || "",
      highway_side: legacyData?.highway_side || "",
      // location_type_id: legacyData?.location_type_id || "",
      // location_type_name: legacyData?.location_type_name || "",
    },

    onSubmit: async () => {
      try {
        // 1️⃣ Sync Formik → Redux
        dispatch(
          setLegacyFields({
            taluka: formik.values.taluka,
            khasra_number: formik.values.khasra_number,
            amenity: formik.values.amenity,
            license_type: formik.values.license_type,
            license_type_selection: licenseTypeSelection,
            licenseExpiry: formik.values.licenseExpiry,
            licenseIssued: formik.values.licenseIssued,
            district: formik.values.district,
            state: formik.values.state,
            pincode: formik.values.pincode,
            village: formik.values.village,
            highway_side: formik.values.highway_side,
            national_highway_number: formik.values.national_highway_number,
            address_1: formik.values.address_1,
            address_2: formik.values.address_2,
            highway_category: formik.values.highway_category,
            legacy_substage_id: formik.values.legacy_substage_id,
            legacy_substage_name:
              filteredSubStageOptions.find(
                (item) =>
                  String(item.id) === String(formik.values.legacy_substage_id),
              )?.name || "",
            // location_type_id: formik.values.location_type_id,
            // location_type_name: formik.values.location_type_name,
            start_chainage_km: formik.values.startKm,
            start_chainage_m: formik.values.startM,
            end_chainage_km: formik.values.endKm,
            end_chainage_m: formik.values.endM,
          }),
        );

        await updateLegacyApplication();
      } catch (error) {
        console.error("Submit failed:", error);
        throw error;
      }
    },
  });

  React.useEffect(() => {
    if (!legacyData) return;

    // Re-run validation once the license type selection has been restored
    formik.validateForm();
  }, [licenseTypeSelection]);

  const handleChainageInputChange =
    (fieldName: "startKm" | "startM" | "endKm" | "endM") =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*\.?\d*$/.test(value)) {
          formik.setFieldValue(fieldName, value);
        }
      };

  const dispatch = useDispatch<AppDispatch>();

  const fetchSubStages = React.useCallback(async () => {
    try {
      const response = await dispatch(fetchLegacySubStagesThunk()).unwrap();
      const options = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setSubStageOptions(options);

      return options;
    } catch (err) {
      console.error(err);
      setSubStageOptions([]);
      return [];
    }
  }, [dispatch]);

  const filteredSubStageOptions = React.useMemo(() => {
    // Show sub-stage for PROVISIONAL_UNDER_REVIEW (PROCESSING group) or FINAL_UNDER_REVIEW (FINAL group)
    if (licenseTypeSelection === "PROVISIONAL_UNDER_REVIEW") {
      return subStageOptions.filter(
        (item) => String(item.group || "").toUpperCase() === "PROCESSING",
      );
    }
    if (licenseTypeSelection === "FINAL_UNDER_REVIEW") {
      return subStageOptions.filter(
        (item) => String(item.group || "").toUpperCase() === "FINAL",
      );
    }
    return [];
  }, [subStageOptions, licenseTypeSelection]);

  const {
    coordinates: geoCoordinates,
    error,
    isLoading,
  } = useGeolocation({
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  });

  const start_coordinates = React.useMemo(
    () => ({
      latitude: start_latitude ?? 0,
      longitude: start_longitude ?? 0,
    }),
    [start_latitude, start_longitude],
  );

  const end_coordinates = React.useMemo(
    () => ({
      latitude: 0,
      longitude: 0,
    }),
    [end_latitude, end_longitude],
  );

  const handleMapClick = React.useCallback(
    (coords: { latitude: number; longitude: number }) => {
      if (!coords?.latitude || !coords?.longitude) return;

      // ✅ update Redux (PRIMARY SOURCE)
      dispatch(
        setLegacyCoordinates({
          site: coordinateSite,
          latitude: coords.latitude,
          longitude: coords.longitude,
          km: 0,
          m: 0,
        }),
      );

      // ✅ sync Formik (for UI fields only)
      formik.setFieldValue(`${coordinateSite}_latitude`, coords.latitude);
      formik.setFieldValue(`${coordinateSite}_longitude`, coords.longitude);
      formik.setFieldValue(`${coordinateSite}_chainage_km`, 0);
      formik.setFieldValue(`${coordinateSite}_chainage_m`, 0);

      // ✅ SAME as stepper behavior
      // if (coordinateSite === "start") {
      //   dispatch(setLegacyCoordinateSite("end"));
      // }
    },
    [dispatch, coordinateSite, formik],
  );

  const isReadOnly = (value: any) => {
    return value !== null && value !== undefined && value !== "";
  };

  React.useEffect(() => {
    const fetchAllCategories = async () => {
      const categoryList = await dispatch(getCategoryListThunk(1));
      if (categoryList.payload.success) {
        const options = categoryList.payload.data.map((cat: any) => ({
          label: cat.name,
          value: cat.name,
          id: cat.id,
        }));
        setAmenityCategoryOptions(options);
      }
    };

    fetchAllCategories();
  }, [dispatch]);

  const isExpired =
    formik.values.licenseExpiry &&
    new Date(formik.values.licenseExpiry) < new Date(new Date().toDateString());

  const initializeLegacyForm = React.useCallback(async () => {
    if (!access) return;

    try {
      dispatch(setLegacyCoordinateSite("start"));

      dispatch(
        setLegacyFields({
          access_id: access,
          start_latitude,
          start_longitude,
          end_latitude,
          end_longitude,
        }),
      );

      const [legacy, stages] = await Promise.all([
        fetchLegacyDetails(access),
        fetchSubStages(),
      ]);

      if (!legacy) return;

      //-------------------------------------------------------
      // Determine license selection
      //-------------------------------------------------------

      let selection:
        | "PROCESSING"
        | "PROVISIONAL_APPROVED"
        | "PROVISIONAL_UNDER_REVIEW"
        | "FINAL"
        | "FINAL_UNDER_REVIEW" = "PROCESSING";

      if (legacy.license_type === "PROVISIONAL") {
        selection = legacy.legacy_substage_id
          ? "PROVISIONAL_UNDER_REVIEW"
          : "PROVISIONAL_APPROVED";
      }

      if (legacy.license_type === "FINAL") {
        selection = legacy.legacy_substage_id ? "FINAL_UNDER_REVIEW" : "FINAL";
      }

      setLicenseTypeSelection(selection);

      //-------------------------------------------------------
      // Filter stage list
      //-------------------------------------------------------

      const filtered =
        selection === "PROVISIONAL_UNDER_REVIEW"
          ? stages.filter(
            (x: any) => String(x.group).toUpperCase() === "PROCESSING",
          )
          : selection === "FINAL_UNDER_REVIEW"
            ? stages.filter(
              (x: any) => String(x.group).toUpperCase() === "FINAL",
            )
            : [];

      //-------------------------------------------------------
      // Prefill substage
      //-------------------------------------------------------

      let matchedStage;

      if (legacy.legacy_substage_id && filtered.length) {
        const decryptedLegacy = await decrypt(legacy.legacy_substage_id);

        const decryptedStages = await Promise.all(
          filtered.map(async (item: any) => ({
            item,
            decryptedId: await decrypt(item.id),
          })),
        );

        matchedStage = decryptedStages.find(
          (x) => x.decryptedId === decryptedLegacy,
        )?.item;
      }

      //-------------------------------------------------------
      // Update Formik once
      //-------------------------------------------------------

      if (matchedStage) {
        formik.setFieldValue("legacy_substage_id", matchedStage.id, false);

        dispatch(
          setLegacyFields({
            legacy_substage_id: matchedStage.id,
            legacy_substage_name: matchedStage.name,
            license_type_selection: selection,
          }),
        );
      }
    } catch (err) {
      console.error(err);
    }
  }, [access, fetchSubStages, dispatch]);

  React.useEffect(() => {
    initializeLegacyForm();
  }, [initializeLegacyForm]);

  React.useEffect(() => {
    if (!access_category_id) return;

    // Category 1 requires organization.
    // Don't load property types when organization is missing.
    if (String(access_category_id) === "1" && !hasOrganization) {
      setAmenityOptions([]);
      formik.setFieldValue("amenity", "");

      dispatch(
        setLegacyFields({
          property_type_id: "",
          property_type_name: "",
        }),
      );

      return;
    }

    fetchAmenityOptions(access_category_id);
  }, [access_category_id, hasOrganization]);

  React.useEffect(() => {
    const hasStoredSelection =
      storedLicenseTypeSelection === "PROCESSING" ||
      storedLicenseTypeSelection === "PROVISIONAL_APPROVED" ||
      storedLicenseTypeSelection === "PROVISIONAL_UNDER_REVIEW" ||
      storedLicenseTypeSelection === "FINAL";

    if (
      hasStoredSelection &&
      storedLicenseTypeSelection !== licenseTypeSelection
    ) {
      setLicenseTypeSelection(
        storedLicenseTypeSelection as
        | "PROCESSING"
        | "PROVISIONAL_APPROVED"
        | "PROVISIONAL_UNDER_REVIEW"
        | "FINAL",
      );
    }
  }, [storedLicenseTypeSelection, licenseTypeSelection]);

  const isOrganizationRequired = React.useCallback(
    (
      categoryId: string | number | undefined,
      propertyTypeId: string | number | undefined,
    ) => {
      if (!categoryId) return false;

      const category = String(categoryId);
      const propertyType = String(propertyTypeId ?? "");

      // Category 1 → organization is always required
      if (category === "1") {
        return true;
      }

      // Category 2 → property types 1 and 2 don't require organization
      if (category === "2") {
        return !["7", "8"].includes(propertyType);
      }

      return false;
    },
    [],
  );

  const isCategoryOneWithoutOrganization =
    !access_category_id ||
    (String(access_category_id) === "1" && !hasOrganization);

  const handleStepSubmit = async () => {
    const selectedCategory = amenityCategoryOptions.find(
      (item) => item.value === formik.values.amenityType,
    );

    const categoryId = selectedCategory?.id ?? access_category_id;

    const propertyTypeId = formik.values.amenity;

    const organizationRequired = isOrganizationRequired(
      categoryId,
      propertyTypeId,
    );

    if (organizationRequired && !hasOrganization) {
      setOpenGstDialog(true);
      return;
    }

    await formik.submitForm();
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== "Enter") return;

    const target = e.target as HTMLElement;
    const tagName = target.tagName.toLowerCase();

    // Keep Enter behavior inside textareas, block implicit form submit elsewhere.
    if (tagName !== "textarea") {
      e.preventDefault();
    }
  };

  React.useEffect(() => {
    setSubmitHandler?.(handleStepSubmit);
  }, []);

  const handleSetStepValid = React.useCallback((value: boolean) => {
    setIsStepValid(value);
  }, []);

  React.useEffect(() => {
    const issuedDate = formik.values.licenseIssued;

    if (!issuedDate) return;

    const issued = new Date(issuedDate);
    const expiry = new Date(issued);

    if (formik.values.license_type === "PROVISIONAL") {
      expiry.setFullYear(expiry.getFullYear() + 1);
    } else if (formik.values.license_type === "FINAL") {
      const june2013 = new Date("2013-06-01");
      const june2020 = new Date("2020-06-01");

      if (issued < june2013) {
        expiry.setFullYear(expiry.getFullYear() + 15);
      } else if (issued >= june2013 && issued <= june2020) {
        expiry.setFullYear(expiry.getFullYear() + 5);
      } else {
        expiry.setFullYear(expiry.getFullYear() + 15);
      }
    }

    expiry.setDate(expiry.getDate() - 1);

    const formattedExpiry = expiry.toISOString().split("T")[0];

    formik.setFieldValue("licenseExpiry", formattedExpiry);
  }, [formik.values.licenseIssued, formik.values.license_type]);

  const stepValid = React.useMemo(() => {
    const isMapSelected = !!start_latitude && !!start_longitude;

    const requiresFile = shouldShowLicenseDocumentForType(
      formik.values.license_type,
    );
    const isSubStageValid =
      filteredSubStageOptions.length === 0 ||
      !!formik.values.legacy_substage_id;

    return (
      formik.isValid &&
      isMapSelected &&
      isSubStageValid &&
      (requiresFile ? !!uploadedFile : true)
    );
  }, [
    formik.isValid,
    uploadedFile,
    start_latitude,
    start_longitude,
    filteredSubStageOptions,
    formik.values.legacy_substage_id,
    formik.values.license_type,
    shouldShowLicenseDocumentForType,
  ]);

  React.useEffect(() => {
    setIsStepValid?.(stepValid);
  }, [stepValid]);

  React.useEffect(() => {
    if (access_category_name) {
      // Check if the category exists in the options
      const exists = amenityCategoryOptions.some(
        (opt) =>
          opt.value === access_category_name || opt.id === access_category_id,
      );

      if (!exists && amenityCategoryOptions.length > 0) {
        // Add the legacy category to options if it doesn't exist
        setAmenityCategoryOptions((prev) => [
          ...prev,
          {
            label: access_category_name,
            value: access_category_name,
            id: access_category_id,
          },
        ]);
      }

      formik.setFieldValue("amenityType", access_category_name);
      dispatch(
        setLegacyFields({
          access_category_id,
          application_category_id: access_category_id,
        }),
      );

      if (
        access_category_id &&
        amenityCategoryOptions.length > 0 &&
        !(String(access_category_id) === "1" && !hasOrganization)
      ) {
        fetchAmenityOptions(access_category_id);
      }
    }
  }, [
    access_category_name,
    access_category_id,
    amenityCategoryOptions.length,
    dispatch,
  ]);

  return (
    <form onSubmit={formik.handleSubmit} onKeyDown={handleFormKeyDown}>
      <Box
        sx={{
          background: "#fff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* SCROLL */}
        <Box sx={{ p: 2.5 }}>
          {/* ---------------- TOP DETAILS ---------------- */}
          <Typography className="body-xs gray-900 py-3 text-center bg-orange-100">
            Dear Applicant, This is legacy application{" "}
            <strong>{legacyData?.application_code}</strong>, you need to update
            the missing/required information to process your application through
            the <strong>Rajmarg Pravesh Portal</strong>.
          </Typography>
          <Typography fontSize={14} fontWeight={600} mt={1}>
            Amenity Details
          </Typography>

          <Divider sx={{ mb: 2, mt: 0.5 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `${labelWidth}px ${fieldWidth}px`,
              rowGap: "12px",
              columnGap: "32px",
              alignItems: "center",
              fontSize: "12px",
            }}
          >
            {/* ADDRESS LINE 1 */}
            <Typography color="#6b7280">
              Address line 1<span className="required">*</span>
            </Typography>
            <StyledTextField
              size="small"
              placeholder="Enter Address line 1"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              {...formik.getFieldProps("address_1")}
              error={
                formik.touched.address_1 && Boolean(formik.errors.address_1)
              }
              helperText={
                formik.touched.address_1
                  ? String(formik.errors.address_1 || "")
                  : ""
              }
            />

            {/* ADDRESS LINE 2 */}
            <Typography color="#6b7280">Address line 2</Typography>
            <StyledTextField
              size="small"
              placeholder="Enter Address line 2"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              {...formik.getFieldProps("address_2")}
              error={
                formik.touched.address_2 && Boolean(formik.errors.address_2)
              }
              helperText={
                formik.touched.address_2
                  ? String(formik.errors.address_2 || "")
                  : ""
              }
            />

            {/* STATE */}
            <Typography color="#6b7280">
              State / Union Territories<span className="required">*</span>
            </Typography>
            <StyledTextField
              size="small"
              placeholder="Enter State"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              value={formik.values.state || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                // Only allow letters, spaces, dots, apostrophes, and hyphens
                const letterRegex = /^[a-zA-Z\s.'-]*$/;
                if (letterRegex.test(value) || value === "") {
                  formik.setFieldValue("state", value);
                }
              }}
              onBlur={formik.handleBlur}
              name="state"
              error={formik.touched.state && Boolean(formik.errors.state)}
              helperText={
                formik.touched.state ? String(formik.errors.state || "") : ""
              }
            />

            {/* DISTRICT */}
            <Typography color="#6b7280">
              District<span className="required">*</span>
            </Typography>
            <StyledTextField
              size="small"
              placeholder="Enter District"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              value={formik.values.district || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                const letterRegex = /^[a-zA-Z\s.'-]*$/;
                if (letterRegex.test(value) || value === "") {
                  formik.setFieldValue("district", value);
                }
              }}
              onBlur={formik.handleBlur}
              name="district"
              error={formik.touched.district && Boolean(formik.errors.district)}
              helperText={
                formik.touched.district ? String(formik.errors.district || "") : ""
              }
            />

            {/* PINCODE */}
            <Typography color="#6b7280">
              Pincode<span className="required">*</span>
            </Typography>
            <StyledTextField
              size="small"
              placeholder="Enter Pincode"
              type="number"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              {...formik.getFieldProps("pincode")}
              error={formik.touched.pincode && Boolean(formik.errors.pincode)}
              helperText={
                formik.touched.pincode
                  ? String(formik.errors.pincode || "")
                  : ""
              }
              inputProps={{
                maxLength: 6,
                pattern: "[0-9]*",
              }}
            />

            {/* HIGHWAY CATEGORY */}
            <Typography color="#6b7280">
              Highway Category<span className="required">*</span>
            </Typography>
            <StyledTextField
              select
              size="small"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              {...formik.getFieldProps("highway_category")}
              error={
                formik.touched.highway_category &&
                Boolean(formik.errors.highway_category)
              }
              helperText={
                formik.touched.highway_category
                  ? String(formik.errors.highway_category || "")
                  : ""
              }
            >
              <MenuItem value="">Select Highway Category</MenuItem>
              <MenuItem value="2-Lane">2-Lane</MenuItem>
              <MenuItem value="4-Lane">4-Lane</MenuItem>
              <MenuItem value="6-Lane">6-Lane</MenuItem>
              <MenuItem value="8-Lane">8-Lane</MenuItem>
            </StyledTextField>

            {/* HIGHWAY NUMBER */}
            <Typography color="#6b7280">
              Highway Number<span className="required">*</span>
            </Typography>
            <StyledTextField
              size="small"
              placeholder="Enter Highway Number"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              {...formik.getFieldProps("national_highway_number")}
              error={
                formik.touched.national_highway_number &&
                Boolean(formik.errors.national_highway_number)
              }
              helperText={
                formik.touched.national_highway_number
                  ? String(formik.errors.national_highway_number || "")
                  : ""
              }
            />

            {/* VILLAGE */}
            <Typography color="#6b7280">
              Village / Town<span className="required">*</span>
            </Typography>
            <StyledTextField
              size="small"
              placeholder="Enter Village"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              value={formik.values.village || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                // Only allow letters, spaces, dots, apostrophes, and hyphens
                const letterRegex = /^[a-zA-Z\s.'-]*$/;
                if (letterRegex.test(value) || value === "") {
                  formik.setFieldValue("village", value);
                }
              }}
              onBlur={formik.handleBlur}
              name="village"
              error={formik.touched.village && Boolean(formik.errors.village)}
              helperText={
                formik.touched.village ? String(formik.errors.village || "") : ""
              }
            />

            {/* SIDE OF AMENITY */}
            <Typography color="#6b7280">
              Side of Amenity<span className="required">*</span>
            </Typography>
            <StyledTextField
              select
              size="small"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              {...formik.getFieldProps("highway_side")}
              error={
                formik.touched.highway_side &&
                Boolean(formik.errors.highway_side)
              }
              helperText={
                formik.touched.highway_side
                  ? String(formik.errors.highway_side || "")
                  : ""
              }
            >
              <MenuItem value="">Select Highway Side</MenuItem>
              <MenuItem value="Left-Hand Side">Left-Hand Side</MenuItem>
              <MenuItem value="Right-Hand Side">Right-Hand Side</MenuItem>
            </StyledTextField>

            {/* <Typography color="#6b7280">
              Stretch Type<span className="required">*</span>
            </Typography>
            <StyledTextField
              select
              size="small"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              value={formik.values.location_type_id || ""}
              onChange={(e: any) => {
                const selectedId = e.target.value;
                const selectedOption = SelectAmenityCategoryRetail.find(
                  (opt: any) => opt.id === Number(selectedId),
                );
                formik.setFieldValue("location_type_id", selectedId);
                if (selectedOption) {
                  formik.setFieldValue(
                    "location_type_name",
                    selectedOption.name,
                  );
                }
              }}
              onBlur={() => formik.setFieldTouched("location_type_id", true)}
              error={
                formik.touched.location_type_id &&
                Boolean(formik.errors.location_type_id)
              }
              helperText={
                formik.touched.location_type_id
                  ? String(formik.errors.location_type_id || "")
                  : ""
              }
            >
              {SelectAmenityCategoryRetail.map((category: any) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </StyledTextField> */}

            {/* ---------------- FORM FIELDS (EXISTING) ---------------- */}

            <Typography color="#6b7280">
              Enter your Taluka<span className="required">*</span>
            </Typography>
            <StyledTextField
              size="small"
              placeholder="Enter Taluka"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              value={formik.values.taluka || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                const alphaNumericRegex = /^[a-zA-Z0-9\s.'-]*$/;
                if (alphaNumericRegex.test(value) || value === "") {
                  formik.setFieldValue("taluka", value);
                }
              }}
              onBlur={formik.handleBlur}
              name="taluka"
              error={formik.touched.taluka && Boolean(formik.errors.taluka)}
              helperText={
                formik.touched.taluka ? String(formik.errors.taluka || "") : ""
              }
            />

            <Typography color="#6b7280">
              Enter your Khasra Number<span className="required">*</span>
            </Typography>
            <StyledTextField
              size="small"
              placeholder="Enter Khasra Number"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              {...formik.getFieldProps("khasra_number")}
              error={
                formik.touched.khasra_number &&
                Boolean(formik.errors.khasra_number)
              }
              helperText={
                formik.touched.khasra_number
                  ? String(formik.errors.khasra_number || "")
                  : ""
              }
            />

            <Typography color="#6b7280">
              Select Type of Amenity/Facility{" "}
              <span className="required">*</span>
            </Typography>
            <StyledTextField
              select
              size="small"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              value={formik.values.amenityType || ""}
              error={
                formik.touched.amenityType && Boolean(formik.errors.amenityType)
              }
              helperText={
                formik.touched.amenityType
                  ? String(formik.errors.amenityType || "")
                  : ""
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const selectedValue = e.target.value;

                const selectedCategory = amenityCategoryOptions.find(
                  (item) => String(item.value) === String(selectedValue),
                );

                const categoryId = selectedCategory?.id;

                // Always keep the selected category visible
                formik.setFieldValue("amenityType", selectedValue);

                // Update category in Redux
                dispatch(
                  setLegacyFields({
                    access_category_id: categoryId,
                    application_category_id: categoryId,
                  }),
                );

                // Category 1 + no organization
                if (String(categoryId) === "1" && !hasOrganization) {
                  // Do NOT allow property type selection
                  formik.setFieldValue("amenityType", "");
                  // formik.setFieldValue("amenity", "");

                  dispatch(
                    setLegacyFields({
                      access_category_id: categoryId,
                      application_category_id: categoryId,
                      property_type_id: "",
                      property_type_name: "",
                    }),
                  );

                  // Show GST/organization dialog
                  setOpenGstDialog(true);

                  // Very important: don't fetch property types
                  setAmenityOptions([]);

                  return;
                }

                // Normal category
                formik.setFieldValue("amenity", "");

                dispatch(
                  setLegacyFields({
                    access_category_id: categoryId,
                    application_category_id: categoryId,
                    property_type_id: "",
                    property_type_name: "",
                  }),
                );

                if (categoryId) {
                  fetchAmenityOptions(categoryId);
                }
              }}
            >
              <MenuItem value="">Select</MenuItem>

              {amenityCategoryOptions.map((option) => (
                <MenuItem key={option.id} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </StyledTextField>

            <Typography color="#6b7280">
              Amenity/Facility<span className="required">*</span>
            </Typography>

            <StyledTextField
              select
              size="small"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              value={formik.values.amenity || ""}
              error={formik.touched.amenity && Boolean(formik.errors.amenity)}
              helperText={
                formik.touched.amenity
                  ? String(formik.errors.amenity || "")
                  : ""
              }
              disabled={isCategoryOneWithoutOrganization}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const selectedPropertyTypeId = e.target.value;

                const categoryId = String(access_category_id);
                const propertyTypeId = String(selectedPropertyTypeId);

                // =====================================================
                // CATEGORY 1
                // Organization is mandatory
                // =====================================================
                if (categoryId === "1" && !hasOrganization) {
                  formik.setFieldValue("amenity", "");
                  formik.setFieldValue("property_type_id", "");

                  dispatch(
                    setLegacyFields({
                      property_type_id: "",
                      property_type_name: "",
                    }),
                  );

                  setOpenGstDialog(true);
                  return;
                }

                // =====================================================
                // CATEGORY 2
                // Property type 7 and 8 are allowed without organization
                // Anything else requires organization
                // =====================================================
                if (
                  categoryId === "2" &&
                  !["7", "8"].includes(propertyTypeId) &&
                  !hasOrganization
                ) {
                  // Do NOT select the restricted property type
                  formik.setFieldValue("amenity", "");
                  formik.setFieldValue("property_type_id", "");

                  dispatch(
                    setLegacyFields({
                      property_type_id: "",
                      property_type_name: "",
                    }),
                  );

                  // Show GST dialog
                  setOpenGstDialog(true);

                  return;
                }

                // =====================================================
                // ALLOWED PROPERTY TYPE
                // =====================================================
                formik.setFieldValue("amenity", selectedPropertyTypeId);

                formik.setFieldValue(
                  "property_type_id",
                  selectedPropertyTypeId,
                );

                const selectedAmenity = amenityOptions.find(
                  (option) =>
                    String(option.value) === String(selectedPropertyTypeId),
                );

                dispatch(
                  setLegacyFields({
                    property_type_id: selectedPropertyTypeId,
                    property_type_name: selectedAmenity?.label || "",
                  }),
                );
              }}
            >
              <MenuItem value="">Select</MenuItem>

              {amenityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </StyledTextField>
            {/* AUTHORITY */}
            <Typography color="#6b7280">
              Select Authority<span className="required">*</span>
            </Typography>

            <StyledTextField
              select
              size="small"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              value={formik.values.authority_id || ""}
              onChange={(e: any) => {
                const authorityId = e.target.value;

                formik.setFieldValue("authority_id", authorityId);

                // reset dependent dropdowns
                formik.setFieldValue("ro_id", "");
                formik.setFieldValue("piu_id", "");

                setRoOptions([]);
                setPiuOptions([]);

                fetchROList(authorityId);
              }}
            >
              {authorityOptions.map((option: any) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </StyledTextField>

            {/* RO */}
            <Typography color="#6b7280">
              Select Regional Office<span className="required">*</span>
            </Typography>

            <StyledTextField
              select
              size="small"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              value={formik.values.ro_id || ""}
              onChange={(e: any) => {
                const roId = e.target.value;

                const officeId = e.target.value;

                formik.setFieldValue("ro_id", officeId);

                // reset PIU
                formik.setFieldValue("piu_id", "");

                setPiuOptions([]);

                const selectedRO = roOptions.find(
                  (item: any) => item.office_id === roId,
                );

                fetchPIUList(selectedRO?.office_id || roId);
              }}
            >
              {roOptions.map((option: any) => (
                <MenuItem key={option.office_id} value={option.office_id}>
                  {option.office_name}
                </MenuItem>
              ))}
            </StyledTextField>

            {/* PIU */}
            <Typography color="#6b7280">
              Select Project Implementation Unit
              <span className="required">*</span>
            </Typography>

            <StyledTextField
              select
              size="small"
              sx={{ width: `${fieldWidth}px`, ...inputSX }}
              value={formik.values.piu_id || ""}
              onChange={(e: any) => {
                formik.setFieldValue("piu_id", e.target.value);
              }}
            >
              {piuOptions.map((option: any) => (
                <MenuItem key={option.office_id} value={option.office_id}>
                  {option.office_name}
                </MenuItem>
              ))}
            </StyledTextField>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
                position: "relative",
              }}
            >
              <Typography color="#6b7280">
                Please select the status of your existing application in the
                legacy (morthnoc.nic.in) system to continue the applicable
                process on Rajmarg Pravesh Portal:
              </Typography>
            </Box>

            <FormControl sx={{ width: `${fieldWidth}px` }}>
              <StyledTextField
                select
                size="small"
                name="license_type"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={licenseTypeSelection}
                onChange={(e: any) => handleLicenseTypeChange(e)}
              >
                <MenuItem value="PROCESSING">
                  My application has only been submitted
                </MenuItem>
                <MenuItem value="PROVISIONAL_UNDER_REVIEW">
                  My application is under review for Provisional Approval and is
                  currently scrutiny, inspection, or payment is under process
                </MenuItem>
                <MenuItem value="PROVISIONAL_APPROVED">
                  My application has already received In-Principle Approval /
                  Provisional Approval
                </MenuItem>
                <MenuItem value="FINAL_UNDER_REVIEW">
                  My application is under review for Final Approval and is
                  currently inspection, or payment is under process
                </MenuItem>
                <MenuItem value="FINAL">
                  My application has already received Final Approval / Final NOC
                </MenuItem>
              </StyledTextField>
            </FormControl>

            {filteredSubStageOptions.length > 0 && (
              <>
                <Typography color="#6b7280">
                  Select Sub-stage<span className="required">*</span>
                </Typography>
                <FormControl sx={{ width: `${fieldWidth}px` }}>
                  <StyledTextField
                    select
                    size="small"
                    name="legacy_substage_id"
                    sx={{ width: `${fieldWidth}px`, ...inputSX }}
                    value={formik.values.legacy_substage_id || ""}
                    onChange={(e: any) => {
                      const selectedId = String(e.target.value);
                      const selectedSubStage = filteredSubStageOptions.find(
                        (item) => String(item.id) === selectedId,
                      );

                      formik.setFieldValue("legacy_substage_id", selectedId);
                      const selectedName = selectedSubStage?.name || "";

                      if (
                        legacy_substage_id !== selectedId ||
                        legacy_substage_name !== selectedName
                      ) {
                        dispatch(
                          setLegacyFields({
                            legacy_substage_id: selectedId,
                            legacy_substage_name: selectedName,
                          }),
                        );
                      }
                    }}
                  >
                    <MenuItem value="">Select Sub-stage</MenuItem>
                    {filteredSubStageOptions.map((item) => (
                      <MenuItem key={item.id} value={String(item.id)}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                  {formik.touched.legacy_substage_id &&
                    formik.errors.legacy_substage_id ? (
                    <Typography color="error" fontSize={12} mt={0.5}>
                      {String(formik.errors.legacy_substage_id)}
                    </Typography>
                  ) : null}
                </FormControl>
              </>
            )}
          </Box>
          {shouldShowLicenseDocumentForType(formik.values.license_type) && (
            <Box mt={3}>
              <Typography fontSize={12} fontWeight={600} mb={1}>
                {shouldShowProvisionalCopy
                  ? "Upload Copy of Provisional NOC/ In principal approval"
                  : formik.values.license_type === "FINAL"
                    ? "Upload Copy of Final NOC/ Final approval"
                    : "Upload Copy of Provisional NOC/ In principal approval"}
              </Typography>
              <Divider sx={{ mb: 1.5 }} />

              <Box
                sx={{
                  background: "#eef2f7",
                  borderRadius: "6px",
                  px: 2,
                  py: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <React.Fragment>
                  {/* HIDDEN INPUT */}
                  <input
                    type="file"
                    id="upload-provisional-license"
                    style={{ display: "none" }}
                    accept=".pdf"
                    onChange={async (event) => {
                      const fileInput = event.target;
                      const file = fileInput.files?.[0];

                      try {
                        if (!file) return;

                        // ✅ Reset input (important for re-upload same file)
                        fileInput.value = "";

                        // ✅ Validate size
                        if (file.size > 21 * 1024 * 1024) {
                          toast.error("File size must be less than 20 MB");
                          return;
                        }

                        // ✅ Step 1: Get Signed URL
                        const fileUploadURL = await dispatch(
                          genericUploadThunk({
                            file,
                            moduleType: "access",
                          }),
                        );

                        if (!fileUploadURL?.payload?.success) {
                          const errorMsg =
                            fileUploadURL?.payload?.response?.data?.details ||
                            fileUploadURL?.payload?.details;

                          toast.error(
                            Array.isArray(errorMsg)
                              ? errorMsg[0]
                              : typeof errorMsg === "string"
                                ? errorMsg
                                : "Something went wrong!",
                          );
                          return;
                        }

                        // ✅ Step 2: Upload to S3
                        if (!productionCheck) {
                          const uploadSuccess = await dispatch(
                            uploadToS3Thunk({
                              signedUrl: fileUploadURL.payload.data.signedUrl,
                              file,
                              contentType: file.type,
                            }),
                          ).unwrap();

                          if (!uploadSuccess) {
                            throw new Error("File upload failed");
                          }
                        }
                        setUploadedFile({
                          name: file.name,
                          objectKey: fileUploadURL.payload.data.objectKey,
                          fileName: fileUploadURL?.payload?.data?.fileName,
                        });
                        // ✅ Step 3: Save File Metadata

                        toast.success("File uploaded successfully");

                        // ✅ OPTIONAL: store in state / formik
                        formik.setFieldValue("provisionalFile", file.name);
                      } catch (error) {
                        console.error(error);
                        toast.error("Upload failed");
                      }
                    }}
                  />

                  {/* UI BOX */}
                  <Box
                    sx={{
                      background: "#eef2f7",
                      borderRadius: "6px",
                      px: 2,
                      py: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box>
                      <Typography fontSize={11}>
                        {formik.values.license_type === "FINAL" &&
                          !isFinalUnderReviewSelection
                          ? "Upload Final License*"
                          : "Upload Provisional License*"}
                      </Typography>
                      <Typography fontSize={10} color="#9ca3af">
                        (Supported Format PDF only)
                      </Typography>
                    </Box>

                    {/* ✅ CONDITIONAL RENDER */}
                    {uploadedFile ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        {/* FILE NAME */}
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "#757575",
                            cursor: "pointer",
                            textDecoration: "underline",
                            "&:hover": {
                              color: "#3A1C73",
                            },
                          }}
                          onClick={async () => {
                            try {
                              const res = await dispatch(
                                getFileSignedURLThunk({
                                  document_name: uploadedFile.fileName,
                                }),
                              );

                              const signedUrl = res.payload?.data?.signedUrl;

                              if (signedUrl) {
                                window.open(
                                  signedUrl,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              } else {
                                alert("Preview not available");
                              }
                            } catch (err) {
                              console.error("Preview error", err);
                            }
                          }}
                          title={uploadedFile.name}
                        >
                          {uploadedFile.name.length > 20
                            ? uploadedFile.name.slice(0, 20) + "..."
                            : uploadedFile.name}
                        </Typography>

                        {/* PREVIEW ICON */}
                        <IconButton
                          sx={{
                            background: "#FFFFFF",
                            borderRadius: "8px",
                            border: "1px solid #D0D5DD",
                            padding: "6px 8px",
                            "&:hover": {
                              backgroundColor: "#E7E4F8",
                            },
                          }}
                        >
                          <RemoveRedEyeOutlinedIcon
                            onClick={async () => {
                              try {
                                const res = await dispatch(
                                  getFileSignedURLThunk({
                                    document_name: uploadedFile.fileName,
                                  }),
                                );

                                const signedUrl = res.payload?.data?.signedUrl;

                                if (signedUrl) {
                                  window.open(
                                    signedUrl,
                                    "_blank",
                                    "noopener,noreferrer",
                                  );
                                } else {
                                  alert("Preview URL not found");
                                }
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                          />
                        </IconButton>

                        {/* DELETE ICON */}
                        <IconButton
                          sx={{
                            background: "#FFFFFF",
                            borderRadius: "8px",
                            border: "1px solid #D0D5DD",
                            padding: "6px 8px",
                            "&:hover": {
                              backgroundColor: "#E7E4F8",
                            },
                          }}
                        >
                          <CloseIcon
                            onClick={async () => {
                              try {
                                // ✅ Step 1: get delete signed URL
                                const deleteRes = await dispatch(
                                  getDeleteSignedURLThunk(
                                    uploadedFile?.fileName,
                                  ),
                                );

                                const signedUrl =
                                  deleteRes.payload?.data?.signedUrl;

                                if (!signedUrl) {
                                  throw new Error("Delete URL not found");
                                }

                                // ✅ Step 2: delete from S3
                                await deleteFileOnAwsAPI(signedUrl);

                                // ✅ Step 4: update UI
                                setUploadedFile(null);
                              } catch (error) {
                                console.error("Delete error:", error);
                              }
                            }}
                          />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography
                        component="label"
                        htmlFor="upload-provisional-license"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: 11,
                          color: "#3a1c73",
                          cursor: "pointer",
                        }}
                      >
                        <UploadFileIcon fontSize="small" />
                        Upload File
                      </Typography>
                    )}
                  </Box>
                </React.Fragment>
              </Box>

              {/* Date */}
              <Typography fontSize={12} fontWeight={600} mt={2}>
                {shouldShowProvisionalCopy
                  ? "Enter Provisional NOC Issued Date"
                  : "Enter Final NOC Issued Date"}
              </Typography>
              <StyledTextField
                fullWidth
                type="date"
                size="small"
                sx={{ mt: 1, width: "100%", ...inputSX }}
                error={
                  formik.touched.licenseIssued &&
                  Boolean(formik.errors.licenseIssued)
                }
                helperText={
                  formik.touched.licenseIssued
                    ? String(formik.errors.licenseIssued || "")
                    : ""
                }
                {...formik.getFieldProps("licenseIssued")}
              />

              <Typography fontSize={12} fontWeight={600} mt={2}>
                {shouldShowProvisionalCopy
                  ? "Enter Provisional NOC Expiry Date"
                  : "Enter Final NOC Expiry Date"}
              </Typography>
              <StyledTextField
                fullWidth
                type="date"
                placeholder="Enter NOC Expiry Date"
                size="small"
                sx={{ mt: 1, width: "100%", ...inputSX }}
                error={
                  formik.touched.licenseExpiry &&
                  Boolean(formik.errors.licenseExpiry)
                }
                helperText={
                  formik.touched.licenseExpiry
                    ? String(formik.errors.licenseExpiry || "")
                    : ""
                }
                {...formik.getFieldProps("licenseExpiry")}
              />
              {isExpired && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: "8px",
                    backgroundColor: "#FFF8E1",
                    border: "1px solid #FFE082",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <InfoIcon
                    sx={{
                      color: "#8A6D3B",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#8A6D3B",
                      lineHeight: 1.5,
                    }}
                  >
                    If an extension to the validity of the{" "}
                    {formik.values.license_type === "PROVISIONAL"
                      ? "Provisional NOC"
                      : "Final NOC"}{" "}
                    has been granted, the revised expiry date may be mentioned.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          <Box mt={3}>
            {/* CONTAINER */}
            <Box
              sx={{
                background: "#eef2f7",
                borderRadius: "6px",
                p: 2,
              }}
            >
              <Typography fontSize={12} fontWeight={600} mb={1}>
                Select Amenity Location on GIS Map and Enter Start and End
                Chainage
              </Typography>
              {/* CHAINAGE INPUTS */}
              <Box>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  sx={{ width: { xs: "100%", sm: "100%", md: "27rem" }, mb: 1 }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    mr={2}
                    position="relative"
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "#fbbf24",
                        position: "relative",
                        top: "6px",
                        border: "1px solid #000000",
                      }}
                    />

                    <Box
                      sx={{
                        width: "2px",
                        height: { xs: "195px", sm: "195px", md: "210px" },
                        background:
                          "repeating-linear-gradient(transparent, transparent 4px, #ccc 4px, #ccc 8px)",
                        marginY: "4px",
                      }}
                    />

                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "#22c55e",
                        position: "relative",
                        bottom: "6px",
                        border: "1px solid #000000",
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={600} fontSize="14px" mb={1}>
                      Enter Start Chainage (KM / M)
                      <span style={{ color: "red" }}>*</span>
                    </Typography>

                    <Box display="flex" gap={1} mb={0.5} alignItems="center">
                      <Box sx={{ width: { xs: "100%", sm: "45%" } }}>
                        <Typography fontSize={11} mb={0.5}>
                          Km
                        </Typography>
                        <StyledTextField
                          size="small"
                          placeholder="Km"
                          sx={{ width: "100%", ...inputSX }}
                          value={formik.values.startKm}
                          onChange={handleChainageInputChange("startKm")}
                          onBlur={formik.handleBlur}
                          name="startKm"
                          inputProps={{
                            inputMode: "decimal",
                            pattern: "[0-9]*[.]?[0-9]*",
                          }}
                          error={
                            formik.touched.startKm &&
                            Boolean(formik.errors.startKm)
                          }
                          helperText={
                            formik.touched.startKm
                              ? String(formik.errors.startKm || "")
                              : ""
                          }
                        />
                      </Box>
                      <span style={{ marginTop: "24px" }}>+</span>
                      <Box sx={{ width: { xs: "100%", sm: "45%" } }}>
                        <Typography fontSize={11} mb={0.5}>
                          m
                        </Typography>
                        <StyledTextField
                          size="small"
                          placeholder="m"
                          sx={{ width: "100%", ...inputSX }}
                          value={formik.values.startM}
                          onChange={handleChainageInputChange("startM")}
                          onBlur={formik.handleBlur}
                          name="startM"
                          inputProps={{
                            inputMode: "decimal",
                            pattern: "[0-9]*[.]?[0-9]*",
                          }}
                          error={
                            formik.touched.startM &&
                            Boolean(formik.errors.startM)
                          }
                          helperText={
                            formik.touched.startM
                              ? String(formik.errors.startM || "")
                              : ""
                          }
                        />
                      </Box>
                    </Box>

                    <Typography
                      fontWeight={600}
                      fontSize="14px"
                      mt={1.5}
                      mb={1}
                    >
                      Enter End Chainage (KM / M)
                      <span style={{ color: "red" }}>*</span>
                    </Typography>

                    <Box display="flex" gap={1} mb={0.5} alignItems="center">
                      <Box sx={{ width: { xs: "100%", sm: "45%" } }}>
                        <Typography fontSize={11} mb={0.5}>
                          Km
                        </Typography>
                        <StyledTextField
                          size="small"
                          placeholder="Km"
                          sx={{ width: "100%", ...inputSX }}
                          value={formik.values.endKm}
                          onChange={handleChainageInputChange("endKm")}
                          onBlur={formik.handleBlur}
                          name="endKm"
                          inputProps={{
                            inputMode: "decimal",
                            pattern: "[0-9]*[.]?[0-9]*",
                          }}
                          error={
                            formik.touched.endKm && Boolean(formik.errors.endKm)
                          }
                          helperText={
                            formik.touched.endKm
                              ? String(formik.errors.endKm || "")
                              : ""
                          }
                        />
                      </Box>
                      <span style={{ marginTop: "24px" }}>+</span>
                      <Box sx={{ width: { xs: "100%", sm: "45%" } }}>
                        <Typography fontSize={11} mb={0.5}>
                          m
                        </Typography>
                        <StyledTextField
                          size="small"
                          placeholder="m"
                          sx={{ width: "100%", ...inputSX }}
                          value={formik.values.endM}
                          onChange={handleChainageInputChange("endM")}
                          onBlur={formik.handleBlur}
                          name="endM"
                          inputProps={{
                            inputMode: "decimal",
                            pattern: "[0-9]*[.]?[0-9]*",
                          }}
                          error={
                            formik.touched.endM && Boolean(formik.errors.endM)
                          }
                          helperText={
                            formik.touched.endM
                              ? String(formik.errors.endM || "")
                              : ""
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", mb: 2 }}>
                  <Typography
                    sx={{
                      color: "#CC8801",
                      fontSize: "14px",
                      fontWeight: 600,
                      mr: 0.5,
                    }}
                  >
                    *
                  </Typography>
                  <Typography
                    sx={{
                      color: "#CC8801",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    Please Pin End Chainage location on the map to continue
                  </Typography>
                </Box>
              </Box>
              <div style={{ width: "40rem", padding: "0 1rem" }}>
                <MapSearchInput />
              </div>
              {/* MAP */}
              <Box
                sx={{
                  // height: 250,
                  borderRadius: "6px",
                  overflow: "hidden",
                  border: "1px solid #d1d5db",
                  background: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  color: "#6b7280",
                }}
              >
                <MapPanel
                  hideRemovePin={false}
                  geoCoordinates={geoCoordinates}
                  coordinates={start_coordinates}
                  chainages={
                    legacyData?.start_chainage_km &&
                      legacyData?.start_chainage_m
                      ? `${legacyData.start_chainage_km}+${legacyData.start_chainage_m}`
                      : ""
                  }
                  onMapClick={handleMapClick}
                  isFirstSectionCompleted={true}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <AlertDialogBox
        open={openGstDialog}
        onClose={() => setOpenGstDialog(false)}
      />
    </form>
  );
};

export default LegacyAmenityDetails;
